"use client";

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { trpc } from "@calcom/trpc/react";
import { showToast } from "@calcom/ui/components/toast";

interface WebPushContextProps {
  permission: NotificationPermission;
  isLoading: boolean;
  isSubscribed: boolean;
  isSupported: boolean;
  isIOSWithoutPWA: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  sendTestPush: () => Promise<void>;
  checkStatus: () => Promise<void>;
}

export const WebPushContext = createContext<WebPushContextProps | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export function WebPushProvider({ children }: ProviderProps) {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "denied"
  );
  const [pushManager, setPushManager] = useState<PushManager | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [isIOSWithoutPWA, setIsIOSWithoutPWA] = useState(false);

  const utils = trpc.useUtils();

  const { mutateAsync: addSubscription } =
    trpc.viewer.loggedInViewerRouter.addNotificationsSubscription.useMutation({
      onSuccess: () => {
        utils.viewer.loggedInViewerRouter.getPushDevices.invalidate();
      },
    });

  const { mutateAsync: removeSubscription } =
    trpc.viewer.loggedInViewerRouter.removeNotificationsSubscription.useMutation({
      onSuccess: () => {
        utils.viewer.loggedInViewerRouter.getPushDevices.invalidate();
      },
    });

  const { mutateAsync: triggerTestPush } =
    trpc.viewer.loggedInViewerRouter.sendTestPush.useMutation();

  const checkStatus = useCallback(async () => {
    if (typeof window === "undefined") return;

    // Feature detection for iOS PWA
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    if (isIOS && !isStandalone) {
      setIsIOSWithoutPWA(true);
    } else {
      setIsIOSWithoutPWA(false);
    }

    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setIsSupported(false);
      return;
    }

    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      setRegistration(reg);
      setPushManager(reg.pushManager);
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
      setPermission(Notification.permission);
    } catch (e) {
      console.warn("Could not register or inspect service worker:", e);
    }
  }, []);

  useEffect(() => {
    checkStatus();

    // Listen to background sync / message from service worker
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "WACLIS_NOTIFICATION_RECEIVED") {
        utils.viewer.loggedInViewerRouter.getNotifications.invalidate();
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleMessage);
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      }
    };
  }, [checkStatus, utils]);

  const detectDeviceLabel = useCallback(() => {
    if (typeof window === "undefined") return "Dispositivo";
    const ua = navigator.userAgent;
    let os = "Desktop";
    if (/android/i.test(ua)) os = "Android";
    else if (/iPad|iPhone|iPod/.test(ua)) os = "iOS";
    else if (/macintosh|mac os x/i.test(ua)) os = "macOS";
    else if (/windows/i.test(ua)) os = "Windows";
    else if (/linux/i.test(ua)) os = "Linux";

    let browser = "Navegador";
    if (/edg/i.test(ua)) browser = "Edge";
    else if (/chrome|crios/i.test(ua)) browser = "Chrome";
    else if (/firefox|fxios/i.test(ua)) browser = "Firefox";
    else if (/safari/i.test(ua)) browser = "Safari";

    return `${os} · ${browser}`;
  }, []);

  const subscribe = useCallback(async () => {
    if (!pushManager) {
      await checkStatus();
    }
    const vapidKey =
      process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ||
      "BBOSVEg1eCGO2yvlLzVi1UlYBI7wk15WA-mHUQ929c2PWj6VTArjfpiOMUxM3ufcyGBO0S11eRrwU7dQf6OciJ4";

    try {
      setIsLoading(true);
      const newPermission = await Notification.requestPermission();
      setPermission(newPermission);

      if (newPermission !== "granted") {
        showToast("Permiso de notificaciones no concedido", "warning");
        return;
      }

      const activeReg = registration || (await navigator.serviceWorker.ready);
      const subscription = await activeReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapidKey),
      });

      const label = detectDeviceLabel();
      const ua = navigator.userAgent;

      await addSubscription({
        subscription: JSON.stringify(subscription),
        deviceLabel: label,
        browser: label.split(" · ")[1] || "Browser",
        operatingSystem: label.split(" · ")[0] || "OS",
        userAgent: ua,
      });

      setIsSubscribed(true);
      showToast("Notificaciones activadas correctamente en este dispositivo", "success");
    } catch (error: any) {
      console.error("Failed to subscribe:", error);
      showToast(
        error?.message ? `Error al activar: ${error.message}` : "No se pudieron activar las notificaciones",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  }, [pushManager, registration, checkStatus, detectDeviceLabel, addSubscription]);

  const unsubscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      const activeReg = registration || (await navigator.serviceWorker.ready);
      const subscription = await activeReg.pushManager.getSubscription();
      if (subscription) {
        const subJson = JSON.stringify(subscription);
        await subscription.unsubscribe();
        await removeSubscription({ subscription: subJson });
      }
      setIsSubscribed(false);
      showToast("Notificaciones desactivadas en este dispositivo", "success");
    } catch (error) {
      console.error("Failed to unsubscribe:", error);
      showToast("Error al desactivar notificaciones", "error");
    } finally {
      setIsLoading(false);
    }
  }, [registration, removeSubscription]);

  const sendTestPush = useCallback(async () => {
    try {
      setIsLoading(true);
      await triggerTestPush();
      showToast("Notificación de prueba enviada", "success");
    } catch (err: any) {
      showToast(err?.message || "Error al enviar notificación de prueba", "error");
    } finally {
      setIsLoading(false);
    }
  }, [triggerTestPush]);

  const contextValue = useMemo(
    () => ({
      permission,
      isLoading,
      isSubscribed,
      isSupported,
      isIOSWithoutPWA,
      subscribe,
      unsubscribe,
      sendTestPush,
      checkStatus,
    }),
    [
      permission,
      isLoading,
      isSubscribed,
      isSupported,
      isIOSWithoutPWA,
      subscribe,
      unsubscribe,
      sendTestPush,
      checkStatus,
    ]
  );

  return <WebPushContext.Provider value={contextValue}>{children}</WebPushContext.Provider>;
}

const urlB64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};
